import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';

const { FiX, FiCheck, FiBookOpen, FiUsers, FiCalendar, FiLoader } = FiIcons;

const statusColor = {
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Completed: 'bg-slate-100 text-slate-500 border-slate-200',
  Draft: 'bg-amber-100 text-amber-700 border-amber-200',
};

const AssignToCampaignModal = ({ module, onClose, onAssign }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dataFetch('read/campaigns').then(res => {
      setCampaigns(res?.data || []);
      setLoading(false);
    });
  }, []);

  const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleAssign = async () => {
    setSaving(true);
    const newAssigned = [...new Set([...(module.assignedCampaigns || []), ...selected])];
    
    await dataFetch(`update/modules/${module.id}`, {
      method: 'PUT',
      body: JSON.stringify({ 
        assignedCampaigns: JSON.stringify(newAssigned), 
        user_id: localStorage.getItem('user_id') 
      })
    });
    
    onAssign(module, selected);
    setSaving(false);
    setDone(true);
    setTimeout(onClose, 1200);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10">

          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 className="text-base font-semibold text-slate-800">Assign to Campaign</h2>
              <p className="text-xs text-slate-500 mt-0.5">"{module?.title}"</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100">
              <SafeIcon icon={FiX} />
            </button>
          </div>

          <div className="px-6 py-4 max-h-80 overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex justify-center py-6"><SafeIcon icon={FiLoader} className="animate-spin text-2xl text-slate-400" /></div>
            ) : campaigns.filter(c => c.status !== 'Completed').length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No active campaigns available.</p>
            ) : campaigns.filter(c => c.status !== 'Completed').map(c => {
              const isSelected = selected.includes(c.id);
              const alreadyAssigned = module?.assignedCampaigns?.includes(c.id);
              
              let learnersCount = 0;
              try { 
                learnersCount = JSON.parse(c.assignedLearners || '[]').length; 
              } catch(e) {
                console.error('Failed to parse assignedLearners', e);
              }

              return (
                <label key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'} ${alreadyAssigned ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" checked={isSelected || alreadyAssigned} disabled={alreadyAssigned} onChange={() => toggle(c.id)} className="accent-blue-600 rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{c.campaignName}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><SafeIcon icon={FiUsers} />{learnersCount} learners</span>
                      <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} />{c.endDate}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${statusColor[c.status]}`}>
                    {alreadyAssigned ? 'Assigned' : c.status}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <span className="text-sm text-slate-500">{selected.length} campaign{selected.length !== 1 ? 's' : ''} selected</span>
            <button onClick={handleAssign} disabled={selected.length === 0 || done || saving}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${done ? 'bg-emerald-500 text-white' : selected.length > 0 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
              {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={done ? FiCheck : FiBookOpen} />}
              {done ? 'Assigned!' : 'Assign Module'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AssignToCampaignModal;